export const CANVAS_VIEWPORT_PARTICIPANT_READY_EVENT =
  'canvas-viewport-participant-ready';

export const CANVAS_VIEWPORT_TARGET_SCROLL_EVENT =
  'canvas-viewport-target-scroll';

export type CanvasViewportTargetPosition = {
  rowAnchor?: CanvasViewportRowAnchor;
  scrollLeft: number;
  scrollTop: number;
};

export type CanvasViewportRowAnchor = {
  offsetPx: number;
  offsetRatio: number;
  rowId: string;
};

export type CanvasViewportRowAnchorRect = {
  height: number;
  left?: number;
  rowId: string;
  top: number;
};

export type CanvasViewportScrollAxis = 'x' | 'y' | 'both';

export type CanvasViewportTargetBinding = {
  targetId: string;
  listenElement?: HTMLElement;
  read: () => CanvasViewportTargetPosition;
  apply: (position: CanvasViewportTargetPosition) => boolean;
};

function clampScroll(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function readAxisPosition(
  element: HTMLElement,
  axis: CanvasViewportScrollAxis
): CanvasViewportTargetPosition {
  return {
    scrollLeft: axis === 'y' ? 0 : element.scrollLeft,
    scrollTop: axis === 'x' ? 0 : element.scrollTop,
  };
}

function scrollPositionForAxis(
  element: HTMLElement,
  axis: CanvasViewportScrollAxis
): CanvasViewportTargetPosition {
  return readAxisPosition(element, axis);
}

function applyScrollPositionForAxis(
  element: HTMLElement,
  position: CanvasViewportTargetPosition,
  axis: CanvasViewportScrollAxis
): boolean {
  const nextLeft =
    axis === 'y' ? element.scrollLeft : clampScroll(position.scrollLeft);
  const nextTop =
    axis === 'x' ? element.scrollTop : clampScroll(position.scrollTop);

  if (
    Math.abs(element.scrollLeft - nextLeft) <= 2 &&
    Math.abs(element.scrollTop - nextTop) <= 2
  ) {
    return false;
  }

  element.scrollLeft = nextLeft;
  element.scrollTop = nextTop;
  return true;
}

function sortedRows(
  rows: readonly CanvasViewportRowAnchorRect[]
): CanvasViewportRowAnchorRect[] {
  return [...rows]
    .filter(row => row.height > 0 && Number.isFinite(row.top))
    .sort((a, b) => {
      const topDelta = a.top - b.top;
      if (Math.abs(topDelta) > 1) {
        return topDelta;
      }
      return (a.left ?? 0) - (b.left ?? 0);
    });
}

export function readRowAnchoredCanvasViewportPosition(
  element: HTMLElement,
  axis: CanvasViewportScrollAxis,
  rows: readonly CanvasViewportRowAnchorRect[]
): CanvasViewportTargetPosition {
  const position = readAxisPosition(element, axis);
  if (axis === 'x') {
    return position;
  }

  const viewportTop = element.scrollTop;
  const anchorRow = sortedRows(rows).find(
    row => row.top + row.height > viewportTop + 1
  );
  if (!anchorRow) {
    return position;
  }

  const offsetPx = viewportTop - anchorRow.top;
  return {
    ...position,
    rowAnchor: {
      offsetPx,
      offsetRatio: offsetPx / anchorRow.height,
      rowId: anchorRow.rowId,
    },
  };
}

export function applyRowAnchoredCanvasViewportPosition(
  element: HTMLElement,
  position: CanvasViewportTargetPosition,
  axis: CanvasViewportScrollAxis,
  rows: readonly CanvasViewportRowAnchorRect[]
): boolean {
  const nextLeft =
    axis === 'y' ? element.scrollLeft : clampScroll(position.scrollLeft);
  let nextTop =
    axis === 'x' ? element.scrollTop : clampScroll(position.scrollTop);

  const anchor = position.rowAnchor;
  if (axis !== 'x' && anchor) {
    const anchorRow = rows.find(row => row.rowId === anchor.rowId);
    if (anchorRow && anchorRow.height > 0) {
      const offsetRatio = Number.isFinite(anchor.offsetRatio)
        ? anchor.offsetRatio
        : anchor.offsetPx / anchorRow.height;
      nextTop = clampScroll(anchorRow.top + offsetRatio * anchorRow.height);
    }
  }

  if (
    Math.abs(element.scrollLeft - nextLeft) <= 2 &&
    Math.abs(element.scrollTop - nextTop) <= 2
  ) {
    return false;
  }

  element.scrollLeft = nextLeft;
  element.scrollTop = nextTop;
  return true;
}

export function createElementCanvasViewportTarget(
  targetId: string,
  element: HTMLElement | undefined,
  axis: CanvasViewportScrollAxis
): CanvasViewportTargetBinding | undefined {
  if (!element) {
    return undefined;
  }
  return {
    targetId,
    listenElement: element,
    read: () => scrollPositionForAxis(element, axis),
    apply: position => applyScrollPositionForAxis(element, position, axis),
  };
}

export function createRowAnchoredCanvasViewportTarget(
  targetId: string,
  element: HTMLElement | undefined,
  axis: CanvasViewportScrollAxis,
  getRows: () => readonly CanvasViewportRowAnchorRect[]
): CanvasViewportTargetBinding | undefined {
  if (!element) {
    return undefined;
  }
  return {
    targetId,
    listenElement: element,
    read: () => readRowAnchoredCanvasViewportPosition(element, axis, getRows()),
    apply: position =>
      applyRowAnchoredCanvasViewportPosition(
        element,
        position,
        axis,
        getRows()
      ),
  };
}

export class CanvasViewportParticipantController {
  private readonly bindings = new Map<string, CanvasViewportTargetBinding>();

  private readonly listenerCleanups: Array<() => void> = [];

  private listenerElements = new Map<string, HTMLElement | undefined>();

  private readonly suppressedTargetScrolls = new Map<
    string,
    CanvasViewportTargetPosition
  >();

  private readonly suppressedTargetScrollTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  constructor(
    private readonly host: HTMLElement,
    private readonly participantName: string,
    private readonly enabled: () => boolean
  ) {}

  applyTarget(
    targetId: string,
    position: CanvasViewportTargetPosition
  ): boolean {
    if (!this.enabled()) {
      return false;
    }
    const changed = this.bindings.get(targetId)?.apply(position) ?? false;
    if (changed) {
      this.suppressNextTargetScroll(targetId);
    }
    return changed;
  }

  dispose(): void {
    this.cleanupListeners();
    this.bindings.clear();
    this.listenerElements.clear();
    delete this.host.dataset.canvasViewportParticipant;
  }

  getTargets(): Record<string, CanvasViewportTargetPosition> {
    if (!this.enabled()) {
      return {};
    }
    return Object.fromEntries(
      [...this.bindings.entries()].map(([targetId, binding]) => [
        targetId,
        binding.read(),
      ])
    );
  }

  notifyTargetsChanged(): void {
    if (!this.enabled()) {
      return;
    }
    this.dispatchReady();
  }

  notifyTargetScroll(targetId: string): void {
    if (!this.enabled()) {
      return;
    }
    const binding = this.bindings.get(targetId);
    if (!binding) {
      return;
    }
    this.dispatchTargetScroll(targetId, binding.read());
  }

  sync(targets: Array<CanvasViewportTargetBinding | undefined>): void {
    if (!this.enabled()) {
      this.dispose();
      return;
    }

    this.host.dataset.canvasViewportParticipant = this.participantName;
    const nextBindings = targets.filter(
      (target): target is CanvasViewportTargetBinding => target != null
    );
    const nextListenerElements = new Map(
      nextBindings.map(binding => [binding.targetId, binding.listenElement])
    );
    const sameListeners =
      nextListenerElements.size === this.listenerElements.size &&
      [...nextListenerElements.entries()].every(
        ([targetId, element]) => this.listenerElements.get(targetId) === element
      );

    this.bindings.clear();
    nextBindings.forEach(binding => {
      this.bindings.set(binding.targetId, binding);
    });

    if (sameListeners) {
      return;
    }

    this.cleanupListeners();
    nextBindings.forEach(binding => {
      const element = binding.listenElement;
      if (!element) {
        return;
      }
      const onScroll = () => {
        if (this.consumeSuppressedTargetScroll(binding.targetId)) {
          return;
        }
        const current = this.bindings.get(binding.targetId);
        if (!current) {
          return;
        }
        this.dispatchTargetScroll(binding.targetId, current.read());
      };
      element.addEventListener('scroll', onScroll, { passive: true });
      this.listenerCleanups.push(() => {
        element.removeEventListener('scroll', onScroll);
      });
    });
    this.listenerElements = nextListenerElements;
    this.dispatchReady();
  }

  private cleanupListeners(): void {
    this.listenerCleanups.forEach(cleanup => cleanup());
    this.listenerCleanups.length = 0;
    this.suppressedTargetScrollTimers.forEach(timer => clearTimeout(timer));
    this.suppressedTargetScrollTimers.clear();
    this.suppressedTargetScrolls.clear();
  }

  private consumeSuppressedTargetScroll(targetId: string): boolean {
    const restored = this.suppressedTargetScrolls.get(targetId);
    if (!restored) {
      return false;
    }
    this.suppressedTargetScrolls.delete(targetId);
    const timer = this.suppressedTargetScrollTimers.get(targetId);
    if (timer) {
      clearTimeout(timer);
      this.suppressedTargetScrollTimers.delete(targetId);
    }
    const current = this.bindings.get(targetId)?.read();
    // Browsers may coalesce a restore's asynchronous scroll event with a later
    // local reveal. Suppress only the restored position, never that newer intent.
    return (
      current?.scrollLeft === restored.scrollLeft &&
      current.scrollTop === restored.scrollTop
    );
  }

  private suppressNextTargetScroll(targetId: string): void {
    const restored = this.bindings.get(targetId)?.read();
    if (!restored) return;
    this.suppressedTargetScrolls.set(targetId, restored);
    const previous = this.suppressedTargetScrollTimers.get(targetId);
    if (previous) {
      clearTimeout(previous);
    }
    this.suppressedTargetScrollTimers.set(
      targetId,
      setTimeout(() => {
        this.suppressedTargetScrolls.delete(targetId);
        this.suppressedTargetScrollTimers.delete(targetId);
      }, 250)
    );
  }

  private dispatchReady(): void {
    this.host.dispatchEvent(
      new CustomEvent(CANVAS_VIEWPORT_PARTICIPANT_READY_EVENT, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private dispatchTargetScroll(
    targetId: string,
    position: CanvasViewportTargetPosition
  ): void {
    this.host.dispatchEvent(
      new CustomEvent(CANVAS_VIEWPORT_TARGET_SCROLL_EVENT, {
        bubbles: true,
        composed: true,
        detail: {
          position,
          targetId,
        },
      })
    );
  }
}
